import { generatePath } from 'react-router-dom';

// 타입 정의
type PathParams = Record<string, string | number>;

// 경로 빌더 인터페이스 - 모든 경로가 동일한 인터페이스 제공
interface PathBuilder<TParams extends PathParams = never> {
	path: string;
	to: [TParams] extends [never] ? () => string : (params: TParams) => string;
}

// 경로 세그먼트를 결합하여 정규화된 경로 생성
const joinPaths = (...segments: string[]): string => {
	const path = segments
		.map((segment) => segment.trim().replace(/^\/+|\/+$/g, ''))
		.filter(Boolean)
		.join('/');
	return `/${path}`;
};

/**
 * 타입 안전한 경로 빌더 생성
 * - 파라미터가 없는 경로: to() 함수 인자 없음
 * - 파라미터가 있는 경로: to(params) 형태로 타입 안전하게 사용
 */
function createPathBuilder<TParams extends PathParams = never>(pathTemplate: string): PathBuilder<TParams> {
	return {
		path: pathTemplate,
		to: ((params?: TParams) => {
			if (!params) return pathTemplate;
			const stringParams = Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
				acc[key] = String(value);
				return acc;
			}, {});

			return generatePath(pathTemplate, stringParams);
		}) as PathBuilder<TParams>['to'],
	};
}

// 중첩 경로 빌더 생성 헬퍼
function nestPaths<T>(root: string, builder: (rootPath: string) => T): T {
	return builder(root);
}

// 루트 경로 상수
export const ROOTS = {
	LOGIN: '/login',
	FIND: '/find',
	HOME: '/',
	DASHBOARD: '/dashboard',
	OPERATION: '/operation',
	PARTNER: '/partner',
	EVENT: '/event',
	SYSTEM: '/system',
	MY: '/my',
} as const;

// 경로 정의
export const paths = {
	// 기본 경로
	login: createPathBuilder(ROOTS.LOGIN),
	home: createPathBuilder(ROOTS.HOME),
	dashboard: createPathBuilder(ROOTS.DASHBOARD),

	// 계정 찾기
	find: nestPaths(ROOTS.FIND, (root) => ({
		email: createPathBuilder(joinPaths(root, 'email')),
		password: createPathBuilder(joinPaths(root, 'password')),
	})),
	// 내 정보
	my: nestPaths(ROOTS.MY, (root) => ({
		profile: createPathBuilder(joinPaths(root, 'profile')),
	})),

	// 운영(콘텐츠) 관리
	operation: nestPaths(ROOTS.OPERATION, (root) => ({
		news: {
			list: createPathBuilder(joinPaths(root, 'news', 'list')),
			new: createPathBuilder(joinPaths(root, 'news', 'new')),
			detail: createPathBuilder<{ newsId: string }>(joinPaths(root, 'news', ':newsId')),
			edit: createPathBuilder<{ newsId: string }>(joinPaths(root, 'news', ':newsId', 'edit')),
		},
		instagram: {
			list: createPathBuilder(joinPaths(root, 'instagram', 'list')),
			new: createPathBuilder(joinPaths(root, 'instagram', 'new')),
			detail: createPathBuilder<{ instagramId: string }>(joinPaths(root, 'instagram', ':instagramId')),
			edit: createPathBuilder<{ instagramId: string }>(joinPaths(root, 'instagram', ':instagramId', 'edit')),
		},
		youtube: {
			list: createPathBuilder(joinPaths(root, 'youtube', 'list')),
			new: createPathBuilder(joinPaths(root, 'youtube', 'new')),
			detail: createPathBuilder<{ youtubeId: string }>(joinPaths(root, 'youtube', ':youtubeId')),
			edit: createPathBuilder<{ youtubeId: string }>(joinPaths(root, 'youtube', ':youtubeId', 'edit')),
		},
		privacyPolicy: {
			list: createPathBuilder(joinPaths(root, 'privacy-policy', 'list')),
			new: createPathBuilder(joinPaths(root, 'privacy-policy', 'new')),
			detail: createPathBuilder<{ policyId: string }>(joinPaths(root, 'privacy-policy', ':policyId')),
		},
		termsOfService: {
			list: createPathBuilder(joinPaths(root, 'terms-of-service', 'list')),
			new: createPathBuilder(joinPaths(root, 'terms-of-service', 'new')),
			detail: createPathBuilder<{ termsId: string }>(joinPaths(root, 'terms-of-service', ':termsId')),
		},
	})),

	// 파트너 관리
	partner: nestPaths(ROOTS.PARTNER, (root) => ({
		// 가입 현황
		join: {
			list: createPathBuilder(joinPaths(root, 'join', 'list')),
			new: createPathBuilder(joinPaths(root, 'join', 'new')),
			detail: createPathBuilder<{ partnerId: string }>(joinPaths(root, 'join', ':partnerId')),
			edit: createPathBuilder<{ partnerId: string }>(joinPaths(root, 'join', ':partnerId', 'edit')),
		},
		// 승인 대기
		pending: {
			list: createPathBuilder(joinPaths(root, 'pending', 'list')),
			detail: createPathBuilder<{ partnerId: string }>(joinPaths(root, 'pending', ':partnerId')),
		},
		// 반려 현황
		rejected: {
			list: createPathBuilder(joinPaths(root, 'rejected', 'list')),
		},
	})),

	// 이벤트 관리
	event: nestPaths(ROOTS.EVENT, (root) => ({
		// QR 발급
		qr: {
			list: createPathBuilder(joinPaths(root, 'qr', 'list')),
			new: createPathBuilder(joinPaths(root, 'qr', 'new')),
			detail: createPathBuilder<{ eventId: string }>(joinPaths(root, 'qr', ':eventId')),
		},
	})),

	// 시스템 (슈퍼관리자 설정)
	system: nestPaths(ROOTS.SYSTEM, (root) => ({
		admin: {
			list: createPathBuilder(joinPaths(root, 'admin', 'list')),
			new: createPathBuilder(joinPaths(root, 'admin', 'new')),
			detail: createPathBuilder<{ adminId: string }>(joinPaths(root, 'admin', ':adminId')),
			edit: createPathBuilder<{ adminId: string }>(joinPaths(root, 'admin', ':adminId', 'edit')),
		},
		log: {
			list: createPathBuilder(joinPaths(root, 'log', 'list')),
		},
	})),
} as const;
